import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { crmQueryKeys } from '@/lib/crm/query/queries';
import { ApiError } from '@/lib/errors';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

export interface UseConvertNoteToTaskResult {
  /** Converts a note to a task via the one-way backend conversion endpoint. A
   * call for a note that already has a conversion in flight is a no-op. */
  convertNoteToTask: (noteId: string) => Promise<void>;
  /** Whether a conversion for this specific note is currently in flight. */
  isConverting: (noteId: string) => boolean;
}

interface ConvertNoteToTaskResponse {
  task_id: string;
  created: boolean;
}

/**
 * Returns `{ convertNoteToTask, isConverting }` for converting a note record
 * to a task via `POST notes/<note_id>/convert-to-task/`.
 *
 * On success, invalidates both the `ahoy_note` and `ahoy_task` object list
 * caches — the note disappears from note surfaces and the new task appears in
 * task surfaces. Each invalidation is independently try/caught so a failure
 * invalidating one query key never prevents the other from running.
 */
export function useConvertNoteToTask(
  orgId: number
): UseConvertNoteToTaskResult {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Set<string>>(new Set());
  // Ref mutations alone don't trigger a re-render, but callers need
  // isConverting() to reflect the in-flight state immediately for the
  // menu item's disabled prop — this state bump forces that re-render.
  const [, forceRender] = useState(0);

  const isConverting = useCallback(
    (noteId: string) => inFlightRef.current.has(noteId),
    []
  );

  const convertNoteToTask = useCallback(
    async (noteId: string) => {
      if (!orgId) {
        return;
      }

      if (inFlightRef.current.has(noteId)) {
        logger.debug(
          'Convert note to task: suppressed duplicate in-flight call',
          {
            noteId,
            orgId,
          }
        );
        return;
      }
      inFlightRef.current.add(noteId);
      forceRender((n: number) => n + 1);

      try {
        await apiPost<ConvertNoteToTaskResponse>(
          `/api/orgs/${orgId}/crm/notes/${noteId}/convert-to-task/`,
          {}
        );
      } catch (error) {
        if (error instanceof ApiError && !error.isServerError()) {
          logger.error('Failed to convert note to task', error, {
            component: 'crm',
            org_id: orgId,
            note_id: noteId,
          });
          toast.error(error.message);
        } else {
          logger.error(
            'Failed to convert note to task',
            error instanceof Error ? error : new Error(String(error)),
            { component: 'crm', org_id: orgId, note_id: noteId }
          );
          toast.error("We couldn't convert this note. Try again.");
        }
        throw error;
      } finally {
        inFlightRef.current.delete(noteId);
        forceRender((n: number) => n + 1);
        try {
          await queryClient.invalidateQueries({
            queryKey: crmQueryKeys.objects(orgId, 'ahoy_note'),
          });
        } catch (invalidateError) {
          logger.error(
            'Failed to invalidate ahoy_note cache after convert-to-task',
            invalidateError instanceof Error
              ? invalidateError
              : new Error(String(invalidateError)),
            { component: 'crm', org_id: orgId, note_id: noteId }
          );
        }
        try {
          await queryClient.invalidateQueries({
            queryKey: crmQueryKeys.objects(orgId, 'ahoy_task'),
          });
        } catch (invalidateError) {
          logger.error(
            'Failed to invalidate ahoy_task cache after convert-to-task',
            invalidateError instanceof Error
              ? invalidateError
              : new Error(String(invalidateError)),
            { component: 'crm', org_id: orgId, note_id: noteId }
          );
        }
      }
    },
    [orgId, queryClient]
  );

  return { convertNoteToTask, isConverting };
}
